/**
 * @typedef {import('@drama/api-sdk').ApiError} ApiError
 * @typedef {import('@drama/api-sdk').TokenStore} TokenStore
 */

/**
 * @param {{
 *   basePath: string,
 *   baseUrl: string,
 *   tokenStore: {
 *     getAccessToken: () => string|null,
 *     getDeviceId: () => string,
 *     refresh: () => Promise<string>,
 *   },
 *   getLocale?: () => string,
 *   fetchImpl?: typeof fetch,
 * }} opts
 */
export function createCallchainHttp(opts) {
  if (!opts || typeof opts.basePath !== 'string') {
    throw new Error('createCallchainHttp: opts.basePath is required')
  }
  if (typeof opts.baseUrl !== 'string') {
    throw new Error('createCallchainHttp: opts.baseUrl is required')
  }
  if (!opts.tokenStore || typeof opts.tokenStore.getAccessToken !== 'function') {
    throw new Error('createCallchainHttp: opts.tokenStore is required')
  }
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch
  if (typeof fetchImpl !== 'function') {
    throw new Error('createCallchainHttp: fetch is unavailable')
  }
  /** @type {Promise<string>|null} */
  let inflightRefresh = null

  function refreshOnce() {
    if (!inflightRefresh) {
      inflightRefresh = Promise.resolve()
        .then(() => opts.tokenStore.refresh())
        .finally(() => {
          inflightRefresh = null
        })
    }
    return inflightRefresh
  }

  /**
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined> }} request
   */
  function buildUrl(path, request) {
    const fullPath = `${opts.basePath}${path}`
    const query = request.query
    if (!opts.baseUrl || opts.baseUrl === '/') {
      return appendQuery(fullPath, query)
    }
    return appendQuery(new URL(fullPath, ensureTrailingBase(opts.baseUrl)).toString(), query)
  }

  /**
   * @param {string} method
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined>, body?: unknown, routeId: string }} request
   */
  async function requestJson(method, path, request) {
    const res = await withTokenRefresh(() => fetchImpl(buildUrl(path, request), {
      method,
      headers: buildHeaders(request),
      body: request.body === undefined ? undefined : JSON.stringify(request.body),
    }))
    if (!res.ok) throw await toError(res)
    if (res.status === 204) return undefined
    return res.json()
  }

  /**
   * @param {string} path
   * @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request
   */
  async function requestText(path, request) {
    const res = await withTokenRefresh(() => fetchImpl(buildUrl(path, request), {
      method: 'GET',
      headers: buildHeaders(request),
    }))
    if (!res.ok) throw await toError(res)
    return res.text()
  }

  async function requestBlob(path, request) {
    const res = await withTokenRefresh(() => fetchImpl(buildUrl(path, request), {
      method: 'GET',
      headers: buildHeaders(request),
    }))
    if (!res.ok) throw await toError(res)
    return res.blob()
  }

  /**
   * Access token 过期时只刷新一次并重放原请求。并发 401 共用同一个
   * refresh promise，避免 refresh token 轮转时旧 token 被重复使用。
   *
   * @param {() => Promise<Response>} send
   */
  async function withTokenRefresh(send) {
    let response = await send()
    if (response.status !== 401) return response
    await refreshOnce()
    response = await send()
    return response
  }

  /**
   * @param {{ body?: unknown, routeId: string }} request
   */
  function buildHeaders(request) {
    /** @type {Record<string, string>} */
    const headers = {
      'x-device-id': opts.tokenStore.getDeviceId(),
      'x-locale': opts.getLocale ? opts.getLocale() : 'en',
      'x-callchain-route-id': request.routeId,
    }
    const token = opts.tokenStore.getAccessToken()
    if (token) headers.authorization = `Bearer ${token}`
    if (request.body !== undefined) headers['content-type'] = 'application/json'
    return headers
  }

  return {
    /** @param {string} path @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request */
    get(path, request) {
      return requestJson('GET', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    post(path, request) {
      return requestJson('POST', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    put(path, request) {
      return requestJson('PUT', path, request)
    },
    /** @param {string} path @param {{ body?: unknown, routeId: string }} request */
    patch(path, request) {
      return requestJson('PATCH', path, request)
    },
    /** @param {string} path @param {{ query?: Record<string, string|number|boolean|null|undefined>, routeId: string }} request */
    delete(path, request) {
      return requestJson('DELETE', path, request)
    },
    getText: requestText,
    getBlob: requestBlob,
  }
}

/**
 * @param {string} url
 * @param {Record<string, string|number|boolean|null|undefined>|undefined} query
 */
function appendQuery(url, query) {
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url
}

/** @param {string} base */
function ensureTrailingBase(base) {
  return base.endsWith('/') ? base : `${base}/`
}

/** @param {Response} res */
async function toError(res) {
  let message = res.statusText || `HTTP ${res.status}`
  let code = 'HTTP_ERROR'
  try {
    const payload = await res.json()
    if (payload.error && typeof payload.error.message === 'string') {
      message = payload.error.message
    }
    if (payload.error && typeof payload.error.code === 'string') {
      code = payload.error.code
    }
  } catch {
    message = res.statusText || `HTTP ${res.status}`
  }
  const error = new Error(message)
  error.code = code
  error.statusCode = res.status
  return error
}
